// ABOUTME: Reconciles confirmed applicant stages across profile and Kanban views.
// ABOUTME: Guards exact profile caches and indexed Kanban reads from stale state.
import { QueryClient, QueryKey } from "react-query";
import { IJobViewStages, IOrderData } from "../useJobContext/JobViewTypes";

export type ExpectedApplicantStage = {
    applicantId: string;
    stageId: string;
    requestId?: string;
};

type ApplicantStageChangeParams = {
    applicantId: string;
    newStageId: string;
    requestId?: string;
};

export const getExpectedApplicantStage = ({
    applicantId,
    newStageId,
    requestId
}: ApplicantStageChangeParams): ExpectedApplicantStage => ({
    applicantId,
    stageId: newStageId,
    requestId
});

export const getCanonicalApplicantStage = (data?: {
    canonicalStage?: { stageId?: string; requestId?: string };
}) => {
    const stage = data?.canonicalStage;
    if (!stage?.stageId || !stage.requestId) {
        return null;
    }
    return {
        stageId: stage.stageId,
        requestId: stage.requestId
    };
};

export const refreshKanbanAfterApplicantStageChange = (
    refreshKanban: (
        message?: string,
        expectedStage?: ExpectedApplicantStage
    ) => Promise<unknown>,
    params: ApplicantStageChangeParams
) => refreshKanban(undefined, getExpectedApplicantStage(params));

export const reconcilePersistedApplicantStage = async ({
    refreshKanban,
    params,
    oldStageId,
    invalidate,
    shouldContinue = () => true
}: {
    refreshKanban: (
        message?: string,
        expectedStage?: ExpectedApplicantStage
    ) => Promise<unknown>;
    params: ApplicantStageChangeParams;
    oldStageId?: string;
    invalidate: () => void;
    shouldContinue?: () => boolean;
}) => {
    if (!shouldContinue()) {
        return false;
    }
    try {
        if (oldStageId === params.newStageId) {
            return await refreshKanban();
        }
        return await refreshKanbanAfterApplicantStageChange(
            refreshKanban,
            params
        );
    } catch {
        if (!shouldContinue()) {
            return false;
        }
        try {
            invalidate();
        } catch {
            return false;
        }
        return false;
    }
};

export const refreshApplicantStageView = async ({
    hasJobContext,
    refreshKanban,
    refreshApplicant,
    invalidateKanban,
    params,
    shouldContinue = () => true
}: {
    hasJobContext: boolean;
    refreshKanban: (
        message?: string,
        expectedStage?: ExpectedApplicantStage
    ) => Promise<unknown>;
    refreshApplicant: () => Promise<unknown>;
    invalidateKanban: () => void;
    params: ApplicantStageChangeParams;
    shouldContinue?: () => boolean;
}) => {
    if (!shouldContinue()) {
        return false;
    }
    await refreshApplicant();
    if (!shouldContinue()) {
        return false;
    }
    if (!hasJobContext) {
        return;
    }

    return reconcilePersistedApplicantStage({
        refreshKanban,
        params,
        invalidate: invalidateKanban,
        shouldContinue
    });
};

export const refreshApplicantProfileAfterStageChange = ({
    queryClient,
    queryKey,
    jobId,
    requestId,
    stage
}: {
    queryClient: QueryClient;
    queryKey: QueryKey;
    jobId: string;
    requestId?: string;
    stage: IJobViewStages;
}) => {
    void queryClient.cancelQueries(
        queryKey,
        { exact: true },
        { revert: false }
    );
    queryClient.setQueryData<Compleo.IObject>(queryKey, (applicantProfile) => {
        if (!Array.isArray(applicantProfile?.data?.Jobs)) {
            return applicantProfile;
        }

        return {
            ...applicantProfile,
            data: {
                ...applicantProfile.data,
                Jobs: applicantProfile.data.Jobs.map((job: Compleo.IObject) => {
                    if (job.JobId !== jobId) {
                        return job;
                    }

                    const profileJob = { ...job };
                    const profileStage = { ...stage };
                    delete profileJob.dateAddedToStage;
                    delete profileStage.actions;
                    if (typeof requestId === "string" && requestId.length > 0) {
                        profileJob.stageChangeRequestId = requestId;
                    } else {
                        delete profileJob.stageChangeRequestId;
                    }

                    return {
                        ...profileJob,
                        PipelineStage: stage.id,
                        Stage: profileStage
                    };
                })
            }
        };
    });
    void queryClient.invalidateQueries(queryKey, {
        exact: true,
        refetchActive: false
    });
};

const retryDelaysMs = [0, 500, 1000, 2000, 4000];

export const normalizeApplicantId = (value: string) =>
    value.split(":").pop() || value;

export const findApplicantIndex = (
    applicants: Compleo.IObject[],
    applicantId: string
) => {
    const normalizedApplicantId = normalizeApplicantId(applicantId);
    return applicants.findIndex(
        (applicant) =>
            typeof applicant?.pk === "string" &&
            normalizeApplicantId(applicant.pk) === normalizedApplicantId
    );
};

export const registerPendingApplicantStage = (
    pendingStages: Map<string, ExpectedApplicantStage>,
    expectedStage: ExpectedApplicantStage
) => {
    if (!expectedStage.requestId) {
        pendingStages.clear();
        return false;
    }

    pendingStages.set(
        normalizeApplicantId(expectedStage.applicantId),
        expectedStage
    );
    return true;
};

export const hasApplicantReachedStage = (
    orderData: IOrderData[],
    expectedStage: ExpectedApplicantStage
) => {
    if (!expectedStage.requestId) {
        return false;
    }

    const applicantId = normalizeApplicantId(expectedStage.applicantId);

    return orderData.some(
        (item) =>
            normalizeApplicantId(item.applicantPK) === applicantId &&
            item.stageId === expectedStage.stageId &&
            item.requestId === expectedStage.requestId
    );
};

export const hasApplicantsReachedStages = (
    orderData: IOrderData[],
    expectedStages: ExpectedApplicantStage[]
) =>
    expectedStages.length > 0 &&
    expectedStages.every((expectedStage) =>
        hasApplicantReachedStage(orderData, expectedStage)
    );

export const waitForApplicantStages = async ({
    getExpectedStages,
    readOrderData,
    shouldContinue = () => true,
    wait = (delayMs) =>
        new Promise((resolve) => {
            setTimeout(resolve, delayMs);
        })
}: {
    getExpectedStages: () => ExpectedApplicantStage[];
    readOrderData: () => Promise<IOrderData[]>;
    shouldContinue?: () => boolean;
    wait?: (delayMs: number) => Promise<void>;
}) => {
    for (const delayMs of retryDelaysMs) {
        if (!shouldContinue()) {
            return null;
        }
        if (delayMs > 0) {
            await wait(delayMs);
        }
        if (!shouldContinue()) {
            return null;
        }

        const orderData = await readOrderData();
        if (!shouldContinue()) {
            return null;
        }
        if (hasApplicantsReachedStages(orderData, getExpectedStages())) {
            return orderData;
        }
    }

    return null;
};
